import { useCallback, useEffect, useRef } from "react"

import { useChatInfo } from "@shared/contexts"

import { Message } from "./useMessages"

interface Props {
  scrollOffset?: number
  messages: Message[] | undefined
  noticeHeight?: number
  disableAutoScroll?: boolean
}

export default function useMessagesRef({
  scrollOffset = 0,
  messages,
  noticeHeight,
  disableAutoScroll = false,
}: Props) {
  const messagesRef = useRef<HTMLDivElement>(null)
  const { isChatLock } = useChatInfo()

  const messageElement = messagesRef.current

  const handleScroll = useCallback(
    (scrollTop: number = 0) => {
      if (!messageElement) return

      messageElement.scrollTop = scrollTop + scrollOffset
    },
    [messageElement, scrollOffset],
  )

  const handleScrollToBottom = useCallback(() => {
    handleScroll(messageElement?.scrollHeight || 0)
  }, [handleScroll, messageElement])

  useEffect(() => {
    if (disableAutoScroll || isChatLock || !messages?.length || !messageElement) return

    handleScrollToBottom()
  }, [
    disableAutoScroll,
    messages,
    isChatLock,
    scrollOffset,
    noticeHeight,
    messageElement,
    handleScrollToBottom,
  ])

  return { messagesRef, handleScroll, handleScrollToBottom }
}
